import React from 'react';
import { Box, Card, CardContent, List, ListItem, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';
import FilterIcon from '@mui/icons-material/FilterList';
import GroupIcon from '@mui/icons-material/Group';
import CheckIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CloseIcon from '@mui/icons-material/Close';

interface FilteringRulesProps {
  filters: any;
}

const getConsentFilterText = (requireConsent: string) => {
  switch (requireConsent) {
    case 'all':
      return 'All contacts (no consent filter)';
    case 'consent':
      return 'Only contacts with consent';
    case 'my_contacts':
      return 'Only my contacts';
    case 'my_contacts_consent':
      return 'Only my contacts with consent';
    default:
      return 'Unknown consent filter';
  }
};

const getOperatorText = (operator: string) => {
  switch (operator) {
    case 'equals': return 'equals';
    case 'not_equals': return 'not equals';
    case 'less_than': return 'less than';
    case 'less_than_equal': return 'less than or equal to';
    case 'greater_than': return 'greater than';
    case 'greater_than_equal': return 'greater than or equal to';
    case 'contains': return 'contains';
    default: return operator;
  }
};

const getOperatorIcon = (operator: string) => {
  switch (operator) {
    case 'equals': return <CheckIcon />;
    case 'not_equals': return <CloseIcon />;
    case 'less_than': return <TrendingDownIcon />;
    case 'less_than_equal': return <TrendingDownIcon />;
    case 'greater_than': return <TrendingUpIcon />;
    case 'greater_than_equal': return <TrendingUpIcon />;
    case 'contains': return <FilterIcon />;
    default: return <CheckIcon />;
  }
};

export const FilteringRules: React.FC<FilteringRulesProps> = ({ filters }) => {
  const theme = useTheme();
  if (!filters) return null;
  return (
    <Card sx={{ height: 400 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.heading?.main ?? theme.palette.secondary.main }}>
            Filtering Rules
          </Typography>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Consent Filter
          </Typography>
          <Box display="flex" alignItems="center">
            <ListItemIcon sx={{ minWidth: 30 }}>
              {filters.requireConsent === 'all' ? (
                <GroupIcon color="primary" />
              ) : filters.requireConsent === 'consent' ? (
                <CheckIcon color="success" />
              ) : (
                <PersonIcon color="info" />
              )}
            </ListItemIcon>
            <Typography variant="body2">
              {getConsentFilterText(filters.requireConsent)}
            </Typography>
          </Box>
        </Box>

        {filters.conditions && filters.conditions.length > 0 ? (
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Data Conditions ({filters.conditionLogic})
            </Typography>
            <List dense>
              {filters.conditions.map((condition: any, index: number) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    {getOperatorIcon(condition.operator)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        <strong>{condition.columnName}</strong> {getOperatorText(condition.operator)} <strong>"{condition.value}"</strong>
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No additional data conditions configured.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default FilteringRules;


